'use client';

import { BlocksRenderer, type BlocksContent } from '@strapi/blocks-react-renderer';
import NextImage from 'next/image';

import { getStrapiURL } from '@/shared/utils/get-strapi-url';
import { cn } from '@/shared/utils/utils';

function resolveMediaUrl(url: string | undefined): string {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const base = getStrapiURL().replace(/\/$/, '');
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
}

interface RichBlocksProps {
  content: BlocksContent | null | undefined;
  className?: string;
}

const headingClasses: Record<number, string> = {
  1: 'mb-2 font-bold text-4xl',
  2: 'mb-2 font-bold text-3xl',
  3: 'mb-2 font-bold text-2xl',
  4: 'mb-2 font-bold text-xl',
  5: 'mb-2 font-bold text-lg',
  6: 'mb-2 font-bold',
};

export function RichBlocks({ content, className }: RichBlocksProps) {
  if (!content || !Array.isArray(content) || content.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex w-full flex-col', className)}>
      <BlocksRenderer
        content={content}
        blocks={{
          paragraph: ({ children }) => <p className="mb-2">{children}</p>,
          heading: ({ children, level }) => {
            const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
            return <Tag className={headingClasses[level]}>{children}</Tag>;
          },
          list: ({ children, format }) => {
            const Tag = format === 'ordered' ? 'ol' : 'ul';
            return (
              <Tag
                className={
                  format === 'ordered' ? 'list-decimal mb-2' : 'list-disc mb-2'
                }
              >
                {children}
              </Tag>
            );
          },
          link: ({ children, url }) => (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-600 underline underline-offset-2 hover:text-sky-700"
            >
              {children}
            </a>
          ),
          quote: ({ children }) => (
            <blockquote className="border-l-4 border-gray-500 pl-4 italic mb-2">
              {children}
            </blockquote>
          ),
          code: ({ plainText }) => (
            <pre className="bg-gray-100 p-4 rounded mb-2 overflow-x-auto">
              <code>{plainText}</code>
            </pre>
          ),
          image: ({ image }) => {
            const url = resolveMediaUrl(image?.url);
            if (!url) return null;
            return (
              <NextImage
                src={url}
                alt={image.alternativeText ?? ''}
                width={image.width}
                height={image.height}
                className="mb-2 w-full h-auto rounded-lg"
              />
            );
          },
        }}
        modifiers={{
          code: ({ children }) => (
            <code className="bg-gray-100 p-1 rounded">{children}</code>
          ),
        }}
      />
    </div>
  );
}
